import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "./ui/sonner";
import { LogOut, CheckCircle2, ShieldAlert, ListTodo, X } from "lucide-react";
import { getGeo } from "../pages/hrms/hrmsShared";

export const ClockWidget = () => {
  const { api, user, lang } = useApp();
  const nav = useNavigate();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const [st, setSt] = useState(null);
  const [gate, setGate] = useState(null);
  const [showGate, setShowGate] = useState(false);
  const [busy, setBusy] = useState(false);

  // 修改原因（2026-10-14 · HR 反馈「HR Dashboard - I am currently unable to use the clock in」）：
  //   原实现返回 MM/DD/YYYY（如 09/29/2026），而 HRMS 其他入口（HR Dashboard / My HR /
  //   Attendance）都用 YYYY-MM-DD。后端 /hrms/overview、/hrms/attendance 一律按
  //   YYYY-MM-DD 查询，导致顶栏打的卡在 HR 看板与月度考勤表里「查无此卡」。
  // 最终效果：与后端存储口径统一为 YYYY-MM-DD，顶栏打卡在看板/考勤表即时可见。
  // 关键取舍：同一函数返回值还用于 /design/todo/gate 的 date 参数，
  //   该接口后端 _day() 同时兼容 YYYY-MM-DD 与 MM/DD/YYYY，故改动不影响下班卡门禁。
  const localDate = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  };
  const localTime = () => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
  };

  const load = useCallback(() => {
    api.get("/hrms/attendance/today", { params: { localDate: localDate() } })
      .then(({ data }) => setSt(data)).catch(() => setSt(null));
  }, [api]);
  const loadGate = useCallback(() => {
    api.get("/design/todo/gate", { params: { date: localDate() } }).then(({ data }) => setGate(data)).catch(() => setGate(null));
  }, [api]);
  useEffect(() => { if (user && user.role !== "subcontractor") load(); }, [load, user]);
  useEffect(() => {
    if (!st?.linked || !st.clockIn || st.clockOut) return;
    loadGate();
    const h = () => loadGate();
    window.addEventListener("design-todo:changed", h);
    return () => window.removeEventListener("design-todo:changed", h);
  }, [st, loadGate]);

  if (!user || user.role === "subcontractor") return null;
  if (!st?.linked) return null;

  const clock = async (action) => {
    setBusy(true);
    try {
      const geo = await getGeo();
      const { data } = await api.post("/hrms/attendance/clock", { action, localDate: localDate(), localTime: localTime(), ...(geo || {}) });
      toast.success(action === "in" ? T(`上班打卡成功 ${localTime()}`, `Clocked in ${localTime()}`) : T(`下班打卡成功 ${localTime()}，辛苦了！`, `Clocked out ${localTime()} — great work!`));
      if (data?.site?.address) toast.success(T(`📍 已匹配工地：${data.site.address}`, `📍 Matched job site: ${data.site.address}`));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || T("打卡失败", "Clock action failed")); }
    setBusy(false);
  };

  if (!st.clockIn) return (
    <button data-testid="topbar-clock-in" disabled={busy} onClick={() => clock("in")}
      className="btn-brand !rounded-full !px-3.5 !py-1.5 text-xs whitespace-nowrap">
      <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70" /><span className="relative inline-flex h-2 w-2 rounded-full bg-white" /></span>
      <span className="hidden 2xl:inline">{T("上班打卡", "Clock in")}</span><span className="2xl:hidden">{T("打卡", "In")}</span>
    </button>
  );
  if (!st.clockOut) {
    const blocked = !!gate?.blocked;
    return (
      <div className="relative flex items-center gap-1.5" data-testid="topbar-clock-status">
        <span className="hidden 2xl:inline-flex items-center gap-1 text-[11px] text-emerald-600 font-semibold"><span className="h-2 w-2 rounded-full bg-emerald-500 pulse-dot" />{st.clockIn} {T("已上班", "on duty")}</span>
        <button data-testid="topbar-clock-out" disabled={busy} data-blocked={blocked ? "1" : "0"}
          onClick={() => { if (blocked) { setShowGate(true); return; } clock("out"); }}
          onMouseEnter={() => blocked && setShowGate(true)}
          title={blocked ? T(`还有 ${gate.pending} 项今日代办未完成，无法下班打卡`, `${gate.pending} design to-dos still open — clock-out locked`) : undefined}
          className={`inline-flex items-center gap-1.5 rounded-full border text-xs font-semibold px-3 py-1.5 transition-colors ${blocked ? "border-red-300 bg-red-50 text-red-600 cursor-not-allowed" : "border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-600 disabled:opacity-50"}`}>
          {blocked ? <ShieldAlert className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}
          <span className="hidden 2xl:inline">{blocked ? T(`下班打卡 · 剩 ${gate.pending} 项`, `Clock out · ${gate.pending} left`) : T("下班打卡", "Clock out")}</span><span className="2xl:hidden">{blocked ? gate.pending : T("下班", "Out")}</span>
        </button>
        {blocked && showGate && (
          <div className="absolute right-0 top-full mt-2 z-50 w-80 rounded-2xl border border-red-200 bg-white shadow-xl p-3 text-left" data-testid="topbar-clock-gate" onMouseLeave={() => setShowGate(false)}>
            {/* 手机没有鼠标移出事件，必须提供明确可点击的关闭入口。 */}
            <button type="button" aria-label={T("关闭打卡提示", "Close clock notice")} onClick={() => setShowGate(false)} className="float-right flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            <p className="text-xs font-bold text-red-700 flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" />{T(`今日代办还有 ${gate.pending} 项未完成（含逾期）`, `${gate.pending} design to-dos due today are still open`)}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{T("完成上交后方可下班打卡；如确有原因，请联系设计部经理改期 / 取消。", "Submit them first, or ask the design manager to postpone / cancel.")}</p>
            <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto" data-testid="topbar-clock-gate-items">
              {(gate.items || []).map((it) => (
                <li key={it.id} className="text-[11.5px] text-slate-700 flex items-start gap-1.5" data-testid={`topbar-clock-gate-item-${it.id}`}>
                  <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${it.overdue ? "bg-red-500" : "bg-amber-400"}`} />
                  <span className="flex-1 min-w-0 truncate">{it.title}</span>
                  <span className={`shrink-0 font-mono ${it.overdue ? "text-red-600 font-bold" : "text-slate-400"}`}>{it.dueDate}{it.dueTime ? ` ${it.dueTime}` : ""}</span>
                </li>
              ))}
            </ul>
            <button data-testid="topbar-clock-gate-go" onClick={() => { setShowGate(false); nav("/design/tasks?tab=todo"); }} className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-lg bg-slate-900 text-white text-xs font-semibold py-1.5 hover:bg-slate-800"><ListTodo className="h-3.5 w-3.5" />{T("去完成今日代办", "Open Daily To-Do")}</button>
          </div>
        )}
      </div>
    );
  }
  return (
    <span data-testid="topbar-clock-done" className="hidden 2xl:inline-flex items-center gap-1 text-[11px] text-emerald-600 font-semibold">
      <CheckCircle2 className="h-3.5 w-3.5" />{st.clockIn}–{st.clockOut} {T("已打卡", "clocked")}
    </span>
  );
};
